import React, { useState, useEffect, useCallback } from "react";
import {
  Paper, Typography, TextField, Button, Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, FormControlLabel, Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import { useNavigate } from "react-router-dom";
import API from "../API/API";
import { getImageUrl } from "../utils/imageHelper";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const api = new API();

/**
 * Dummy (draft) submissions list. Move = hand the entry to the real Saved Submissions with a room.
 * The real Saved Submissions page and data are not touched by anything here except that Move.
 */
export default function DummySubmissions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [showMoved, setShowMoved] = useState(false);
  const [openImage, setOpenImage] = useState(null);

  // Move dialog
  const [moveItem, setMoveItem] = useState(null);
  const [moveRoom, setMoveRoom] = useState("");
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (date) params.date = date;
      if (!showMoved) params.status = "DRAFT";
      const res = await api.get("api/guest-image-drafts", params);
      const list = res?.data?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, date, showMoved]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete dummy entry of ${row.phone_number}?`)) return;
    try {
      await api.delete(`api/guest-image-drafts/${row.id}`);
      toast.success("Dummy entry deleted");
      fetchRows();
    } catch (e) { /* API helper shows the error */ }
  };

  const openMove = async (row) => {
    setMoveItem(row);
    setMoveRoom(row.room_number ? String(row.room_number) : "");
    setRoomsLoading(true);
    try {
      const res = await api.get("api/guest-images/available-rooms");
      const raw = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
      setRooms(raw.map((r) => String(typeof r === "object" ? (r.room_number || r.room_no || "") : r)).filter(Boolean));
    } catch (e) {
      setRooms([]);
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleMove = async () => {
    if (!moveRoom) { toast.warning("Select a room number"); return; }
    setMoving(true);
    try {
      const res = await api.post(`api/guest-image-drafts/${moveItem.id}/move`, { room_number: moveRoom });
      toast.success(res?.data?.message || "Moved to Saved Submissions");
      setMoveItem(null);
      fetchRows();
    } catch (e) { /* API helper shows the error */ } finally {
      setMoving(false);
    }
  };

  const th = { fontWeight: 700, color: "#24292f", fontSize: "0.8rem", whiteSpace: "nowrap", bgcolor: "#f4f6f8" };

  return (
    <Box sx={{ width: "100%", mt: { xs: 0.5, md: 2 }, mb: 3, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: "#6a1b9a", fontSize: { xs: "1.1rem", md: "1.5rem" } }}>Dummy Submissions</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/dummy-entry")}
          sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#6a1b9a", "&:hover": { bgcolor: "#4a148c" } }}>
          Add Dummy Entry
        </Button>
      </Box>

      <Paper elevation={0} sx={{ p: 2, border: "1px solid #d0d7de", borderRadius: 2 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
          <TextField size="small" placeholder="Search phone, guest, room" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
          <TextField size="small" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <FormControlLabel control={<Checkbox size="small" checked={showMoved} onChange={(e) => setShowMoved(e.target.checked)} />}
            label={<Typography sx={{ fontSize: "0.85rem" }}>Show moved</Typography>} />
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows} sx={{ textTransform: "none" }}>Refresh</Button>
        </Box>

        <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={th}>Phone / Guest</TableCell>
                <TableCell sx={th}>Room</TableCell>
                <TableCell sx={th}>Images</TableCell>
                <TableCell sx={th}>Status</TableCell>
                <TableCell sx={th}>User / Date</TableCell>
                <TableCell sx={th} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6c757d" }}>No dummy submissions</TableCell></TableRow>
              ) : rows.map((r) => {
                const moved = r.status === "MOVED";
                return (
                  <TableRow key={r.id} hover sx={{ opacity: moved ? 0.6 : 1 }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{r.phone_number}</Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "#4b5563" }}>{r.guest_name || "-"}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.room_number || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {(r.images || []).map((img, i) => (
                          <Box key={i} component="img" src={getImageUrl(img)} onClick={() => setOpenImage(getImageUrl(img))}
                            sx={{ width: 34, height: 34, borderRadius: 1, objectFit: "cover", border: "1px solid #d0d7de", cursor: "pointer" }} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={moved ? "MOVED" : "DUMMY"}
                        sx={{ fontWeight: 700, fontSize: "0.65rem", color: "#fff", bgcolor: moved ? "#2e7d32" : "#6a1b9a" }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.78rem" }}>{r.user_name || "-"}</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#6c757d" }}>{r.created_at ? dayjs(r.created_at).format("DD-MM-YYYY hh:mm A") : ""}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {!moved && (
                        <>
                          <Tooltip title="Move to Saved Submissions">
                            <Button size="small" variant="contained" color="success" startIcon={<DriveFileMoveIcon />} onClick={() => openMove(r)}
                              sx={{ textTransform: "none", fontSize: "0.72rem", mr: 0.5 }}>
                              Move
                            </Button>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => navigate("/dummy-entry", { state: { editDraft: r } })}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(r)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(moveItem)} onClose={() => !moving && setMoveItem(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Move to Saved Submissions</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", mb: 1.5 }}>
            {moveItem?.phone_number} {moveItem?.guest_name ? `· ${moveItem.guest_name}` : ""} ({(moveItem?.images || []).length} images)
          </Typography>
          <Autocomplete options={rooms} loading={roomsLoading} value={moveRoom || null}
            onChange={(e, v) => setMoveRoom(v || "")}
            renderInput={(params) => <TextField {...params} size="small" label="Room Number *" />} />
          <Typography variant="caption" sx={{ color: "#6c757d", display: "block", mt: 1 }}>
            Only rooms free for an ID upload are listed (same as Add Guest Image).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveItem(null)} disabled={moving} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleMove} disabled={moving || !moveRoom} sx={{ textTransform: "none", fontWeight: 700 }}>
            {moving ? <CircularProgress size={18} color="inherit" /> : "Move"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(openImage)} onClose={() => setOpenImage(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 1, display: "flex", justifyContent: "center" }}>
          {openImage && <Box component="img" src={openImage} sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
